/**
 * Listing View module.
 * Handles AJAX filtering, live search, crawlable pagination, and URL synchronization.
 */

export const initListingView = () => {
	const blocks = document.querySelectorAll('.listing-view');
	if (!blocks.length) return;

	blocks.forEach((block) => {
		if (block.dataset.lvInitialized) return;
		block.dataset.lvInitialized = 'true';

		const dropdowns = block.querySelectorAll('.lv-filter-dropdown');
		const gridWrap = block.querySelector('.lv-grid-wrap');
		const gridContainer = block.querySelector('.lv-grid');
		const paginationWrap = block.querySelector('.lv-pagination-wrap');
		const searchInput = block.querySelector('.lv-search-input');

		let currentAbortController = null;
		let searchTimeout = null;
		let currentPage = 1;

		// 1. Setup Dropdown Toggles
		dropdowns.forEach((dropdown) => {
			const btn = dropdown.querySelector('.lv-filter-btn');
			const menu = dropdown.querySelector('.lv-filter-menu');

			if (!btn || !menu) return;

			btn.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();

				const isOpen = menu.classList.contains('is-open');

				// Close all other dropdowns in this block
				dropdowns.forEach((otherDropdown) => {
					if (otherDropdown !== dropdown) {
						const otherBtn = otherDropdown.querySelector('.lv-filter-btn');
						const otherMenu = otherDropdown.querySelector('.lv-filter-menu');
						if (otherBtn) {
							otherBtn.classList.remove('is-active');
							otherBtn.setAttribute('aria-expanded', 'false');
						}
						if (otherMenu) {
							otherMenu.classList.remove('is-open');
							otherMenu.setAttribute('aria-hidden', 'true');
						}
					}
				});

				// Toggle current dropdown
				if (isOpen) {
					btn.classList.remove('is-active');
					btn.setAttribute('aria-expanded', 'false');
					menu.classList.remove('is-open');
					menu.setAttribute('aria-hidden', 'true');
				} else {
					btn.classList.add('is-active');
					btn.setAttribute('aria-expanded', 'true');
					menu.classList.add('is-open');
					menu.setAttribute('aria-hidden', 'false');
				}
			});

			menu.addEventListener('click', (e) => {
				e.stopPropagation();
			});
		});

		// Close dropdowns on outside click
		document.addEventListener('click', () => {
			dropdowns.forEach((dropdown) => {
				const btn = dropdown.querySelector('.lv-filter-btn');
				const menu = dropdown.querySelector('.lv-filter-menu');
				if (btn && menu) {
					btn.classList.remove('is-active');
					btn.setAttribute('aria-expanded', 'false');
					menu.classList.remove('is-open');
					menu.setAttribute('aria-hidden', 'true');
				}
			});
		});

		// Close dropdowns on Escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				dropdowns.forEach((dropdown) => {
					const btn = dropdown.querySelector('.lv-filter-btn');
					const menu = dropdown.querySelector('.lv-filter-menu');
					if (btn && menu) {
						btn.classList.remove('is-active');
						btn.setAttribute('aria-expanded', 'false');
						menu.classList.remove('is-open');
						menu.setAttribute('aria-hidden', 'true');
					}
				});
			}
		});

		// Update dropdown button label based on selections
		const updateDropdownLabels = () => {
			dropdowns.forEach((dropdown) => {
				const btnText = dropdown.querySelector('.lv-filter-btn-text');
				const btn = dropdown.querySelector('.lv-filter-btn');
				if (!btnText || !btn) return;

				const defaultText = btnText.dataset.defaultText || btnText.textContent.trim();
				const checked = dropdown.querySelectorAll('input[type="checkbox"]:checked');

				if (checked.length === 0) {
					btnText.textContent = defaultText;
					btn.classList.remove('has-selection');
				} else if (checked.length === 1) {
					const labelEl = checked[0].closest('.lv-checkbox-label');
					const nameEl = labelEl ? labelEl.querySelector('.lv-option-name') : null;
					btnText.textContent = nameEl ? nameEl.textContent.trim() : `${defaultText} (1)`;
					btn.classList.add('has-selection');
				} else {
					btnText.textContent = `${defaultText} (${checked.length})`;
					btn.classList.add('has-selection');
				}
			});
		};

		// Synchronize URL with active filters and page (Requirement: Pre-filtered views linkable by URL)
		const updateUrl = (page = 1) => {
			const url = new URL(window.location.href);
			const params = url.searchParams;

			// Reset taxonomy parameters
			['topic', 'resource_types', 'resource_type', 'industry', 'service', 'business_line'].forEach((taxKey) => {
				params.delete(taxKey);
				params.delete(`${taxKey}[]`);
			});
			params.delete('s');
			params.delete('paged');

			// Search
			if (searchInput && searchInput.value.trim()) {
				params.set('s', searchInput.value.trim());
			}

			// Group checked checkboxes by taxonomy
			const selectedTax = {};
			const checkboxes = block.querySelectorAll('.lv-filter-menu input[type="checkbox"]');
			checkboxes.forEach((cb) => {
				if (cb.checked && cb.name && cb.value) {
					const cleanName = cb.name.replace('[]', '');
					if (!selectedTax[cleanName]) {
						selectedTax[cleanName] = [];
					}
					selectedTax[cleanName].push(cb.value.trim());
				}
			});

			// Set comma-separated query parameters like ?service=service-tag,service-tag-1
			Object.keys(selectedTax).forEach((taxKey) => {
				if (selectedTax[taxKey].length > 0) {
					params.set(taxKey, selectedTax[taxKey].join(','));
				}
			});

			// Page (only set if > 1)
			if (page > 1) {
				params.set('paged', page.toString());
			}

			// Decode %2C for clean human-readable URLs
			let queryString = params.toString();
			if (queryString) {
				queryString = queryString.replace(/%2C/gi, ',');
			}
			const newUrl = url.pathname + (queryString ? `?${queryString}` : '') + url.hash;
			window.history.pushState({ path: newUrl }, '', newUrl);
		};

		// Helper: scroll down to the listing view block
		const scrollToBlock = () => {
			const offset = 120;
			const blockTop = block.getBoundingClientRect().top + window.pageYOffset - offset;
			window.scrollTo({
				top: Math.max(0, blockTop),
				behavior: 'smooth',
			});
		};

		// 2. Perform AJAX Query
		const fetchListingPosts = (targetPage = 1, shouldScroll = false, shouldUpdateUrl = true) => {
			currentPage = targetPage;
			updateDropdownLabels();

			if (shouldUpdateUrl) {
				updateUrl(targetPage);
			}

			if (currentAbortController) {
				currentAbortController.abort();
			}
			currentAbortController = new AbortController();

			const formData = new FormData();
			formData.append('action', 'lv_filter_posts');

			const nonce = block.dataset.nonce || (typeof window !== 'undefined' && window.custom_ajax_object ? window.custom_ajax_object.nonce : '');
			formData.append('nonce', nonce);

			const postType = block.dataset.postType || 'post';
			formData.append('post_type', postType);

			const itemsPerPage = block.dataset.itemsPerPage || '9';
			formData.append('items_per_page', itemsPerPage);

			formData.append('paged', targetPage.toString());

			// Search query
			if (searchInput && searchInput.value.trim()) {
				formData.append('s', searchInput.value.trim());
			}

			// Filter checkboxes grouped by taxonomy
			const checkboxes = block.querySelectorAll('.lv-filter-menu input[type="checkbox"]');
			const selectedTax = {};

			checkboxes.forEach((cb) => {
				if (cb.checked && cb.name && cb.value) {
					const cleanName = cb.name.replace('[]', '');
					if (!selectedTax[cleanName]) {
						selectedTax[cleanName] = [];
					}
					selectedTax[cleanName].push(cb.value.trim());
				}
			});

			// Send both comma-separated and array format for maximum compatibility
			Object.keys(selectedTax).forEach((taxKey) => {
				const values = selectedTax[taxKey];
				if (values.length > 0) {
					formData.append(taxKey, values.join(','));
					values.forEach((val) => {
						formData.append(`${taxKey}[]`, val);
					});
				}
			});

			// Presets fallback
			if (!selectedTax['topic'] && block.dataset.topicPreset) {
				formData.append('topic_preset', block.dataset.topicPreset);
			}
			if (!selectedTax['resource_types'] && !selectedTax['resource_type'] && (block.dataset.resourceTypesPreset || block.dataset.resourceTypePreset)) {
				formData.append('resource_types_preset', block.dataset.resourceTypesPreset || block.dataset.resourceTypePreset);
			}
			if (!selectedTax['industry'] && block.dataset.industryPreset) {
				formData.append('industry_preset', block.dataset.industryPreset);
			}
			if (!selectedTax['service'] && block.dataset.servicePreset) {
				formData.append('service_preset', block.dataset.servicePreset);
			}
			if (!selectedTax['business_line'] && block.dataset.businessLinePreset) {
				formData.append('business_line_preset', block.dataset.businessLinePreset);
			}

			const ajaxUrl = block.dataset.ajaxUrl || (typeof window !== 'undefined' && window.custom_ajax_object ? window.custom_ajax_object.ajax_url : '/wp-admin/admin-ajax.php');

			if (gridWrap) {
				gridWrap.classList.add('is-loading');
			}

			const startTime = Date.now();
			const minDelay = 200;

			fetch(ajaxUrl, {
				method: 'POST',
				body: formData,
				signal: currentAbortController.signal,
			})
				.then((response) => {
					if (!response.ok) {
						throw new Error(`HTTP error! status: ${response.status}`);
					}
					return response.json();
				})
				.then((res) => {
					const elapsed = Date.now() - startTime;
					const remaining = Math.max(0, minDelay - elapsed);

					setTimeout(() => {
						if (res && res.success && res.data) {
							if (gridContainer && typeof res.data.html !== 'undefined') {
								gridContainer.innerHTML = res.data.html;
							}
							if (paginationWrap && typeof res.data.pagination_html !== 'undefined') {
								paginationWrap.innerHTML = res.data.pagination_html;
							}
						}

						if (gridWrap) {
							gridWrap.classList.remove('is-loading');
						}

						if (shouldScroll) {
							scrollToBlock();
						}
					}, remaining);
				})
				.catch((err) => {
					if (err.name !== 'AbortError') {
						console.error('Listing View AJAX error:', err);
						if (gridWrap) {
							gridWrap.classList.remove('is-loading');
						}
					}
				});
		};

		// 3. Filter Checkbox Listeners (Requirement: Applying a filter resets to page 1)
		const filterCheckboxes = block.querySelectorAll('.lv-filter-menu input[type="checkbox"]');
		filterCheckboxes.forEach((cb) => {
			cb.addEventListener('change', () => {
				fetchListingPosts(1, false, true);
			});
		});

		// 4. Live Search Listener (Debounced, resets to page 1)
		if (searchInput) {
			searchInput.addEventListener('input', () => {
				clearTimeout(searchTimeout);
				searchTimeout = setTimeout(() => {
					fetchListingPosts(1, false, true);
				}, 350);
			});

			searchInput.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					clearTimeout(searchTimeout);
					fetchListingPosts(1, false, true);
				}
			});
		}

		// 5. Pagination Click Listener (Delegated on crawlable links)
		if (paginationWrap) {
			paginationWrap.addEventListener('click', (e) => {
				const link = e.target.closest('[data-page], .page-numbers:not(.current):not(.dots), .lv-page-btn[data-page]');
				if (link && !link.classList.contains('disabled') && !link.classList.contains('current') && !link.classList.contains('dots')) {
					e.preventDefault();
					let targetPage = parseInt(link.dataset.page, 10);
					if (!targetPage) {
						const textNum = parseInt(link.textContent.trim(), 10);
						if (textNum) targetPage = textNum;
					}
					if (targetPage && targetPage !== currentPage) {
						fetchListingPosts(targetPage, true, true);
					}
				}
			});
		}

		// 6. Browser Navigation (Back/Forward popstate handler)
		window.addEventListener('popstate', () => {
			const urlParams = new URLSearchParams(window.location.search);
			const pageFromUrl = parseInt(urlParams.get('paged') || '1', 10);

			// Sync search
			if (searchInput) {
				searchInput.value = urlParams.get('s') || '';
			}

			// Sync checkboxes: support comma-separated values like ?service=tag1,tag2 and repeated params
			filterCheckboxes.forEach((cb) => {
				const paramName = cb.name.replace('[]', '');
				const values = [];

				const directVal = urlParams.get(paramName) || (paramName === 'resource_types' ? urlParams.get('resource_type') : null);
				if (directVal) {
					directVal.split(',').forEach((v) => {
						const trimmed = v.trim().toLowerCase();
						if (trimmed) values.push(trimmed);
					});
				}

				urlParams.getAll(paramName).forEach((v) => {
					v.split(',').forEach((sub) => {
						const trimmed = sub.trim().toLowerCase();
						if (trimmed) values.push(trimmed);
					});
				});

				urlParams.getAll(`${paramName}[]`).forEach((v) => {
					v.split(',').forEach((sub) => {
						const trimmed = sub.trim().toLowerCase();
						if (trimmed) values.push(trimmed);
					});
				});

				if (paramName === 'resource_types') {
					urlParams.getAll('resource_type').forEach((v) => {
						v.split(',').forEach((sub) => {
							const trimmed = sub.trim().toLowerCase();
							if (trimmed) values.push(trimmed);
						});
					});
					urlParams.getAll('resource_type[]').forEach((v) => {
						v.split(',').forEach((sub) => {
							const trimmed = sub.trim().toLowerCase();
							if (trimmed) values.push(trimmed);
						});
					});
				}

				cb.checked = values.includes(cb.value.trim().toLowerCase());
			});

			fetchListingPosts(pageFromUrl, false, false);
		});

		// Normalize initial URL if it has repeated params (e.g. ?service=tag1&service=tag2) or bracket keys
		const normalizeInitialUrl = () => {
			const currentSearch = window.location.search;
			if (!currentSearch) return;

			const urlParams = new URLSearchParams(currentSearch);
			let needsReplace = false;

			['topic', 'resource_types', 'resource_type', 'industry', 'service', 'business_line'].forEach((taxKey) => {
				const values = [];
				const directVal = urlParams.get(taxKey);
				if (directVal) {
					directVal.split(',').forEach((v) => {
						const trimmed = v.trim();
						if (trimmed) values.push(trimmed);
					});
				}

				urlParams.getAll(taxKey).forEach((v) => {
					v.split(',').forEach((sub) => {
						const trimmed = sub.trim();
						if (trimmed) values.push(trimmed);
					});
				});

				urlParams.getAll(`${taxKey}[]`).forEach((v) => {
					v.split(',').forEach((sub) => {
						const trimmed = sub.trim();
						if (trimmed) values.push(trimmed);
					});
				});

				const uniqueValues = [...new Set(values)];

				// Check if URL has repeated keys or bracket keys
				if (urlParams.has(`${taxKey}[]`) || urlParams.getAll(taxKey).length > 1) {
					needsReplace = true;
					urlParams.delete(`${taxKey}[]`);
					urlParams.delete(taxKey);
					if (uniqueValues.length > 0) {
						urlParams.set(taxKey, uniqueValues.join(','));
					}
				}
			});

			if (needsReplace) {
				let queryString = urlParams.toString();
				if (queryString) {
					queryString = queryString.replace(/%2C/gi, ',');
				}
				const newUrl = window.location.pathname + (queryString ? `?${queryString}` : '') + window.location.hash;
				window.history.replaceState({ path: newUrl }, '', newUrl);
			}
		};

		normalizeInitialUrl();

		// Initial label update based on any pre-selected terms
		updateDropdownLabels();

		// Scroll down to the listing view block if URL has filter, search, or pagination parameters
		const hasFilterParams = () => {
			const search = window.location.search;
			if (!search) return false;
			const params = new URLSearchParams(search);
			const filterKeys = ['topic', 'resource_types', 'resource_type', 'industry', 'service', 'business_line', 'topic[]', 'resource_types[]', 'resource_type[]', 'industry[]', 'service[]', 'business_line[]', 's', 'paged'];
			return filterKeys.some((k) => params.has(k) && params.get(k).trim() !== '');
		};

		if (hasFilterParams()) {
			// Delay slightly to ensure page layout, hero, and fonts are rendered
			setTimeout(() => {
				scrollToBlock();
			}, 200);
		}
	});
};

// Initialize immediately or on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initListingView);
} else {
	initListingView();
}
